import { Component, effect, inject, OnInit, Signal, ViewChild } from '@angular/core';

import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { NavBarComponent } from '@shared/components/organisms/nav-bar/nav-bar.component';
import { FooterComponent } from '@shared/components/organisms/footer/footer.component';
import { SidePanelComponent } from '@shared/components/organisms/side-panel/side-panel.component';
import { UserStore } from '@shared/stores/user.store';
import { enableMapSet } from 'immer';
import { ErrorModalService } from '@shared/services/error-modal.service';
import { filter } from 'rxjs';
import {
  NewsFeedBannerComponent
} from '@features/admin/components/news-feed/news-feed-banner/news-feed-banner.component';
import { PdfViewerModalComponent } from '@shared/components/organisms/pdf-viewer-modal/pdf-viewer-modal.component';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { DialogComponent } from '@shared/components/organisms/dialog/dialog.component';
import { environment } from '@env/environment';
import { UserInactivityService } from '@shared/services/user-inactivity.service';
import { CommonStore } from '@shared/stores/common.store';
import { InactivityWarningOrchestratorService } from '@shared/services/inactivity-warning-orchestrator.service';
import { SubscriptionStore } from '@features/profile/store/subscription.store';
import { Button } from '@shared/components/atoms/button/models/button.model';
import { CommonFunctionsService } from '@shared/services/common-functions.service';
import { CompanyStore } from '@features/profile/store/company.store';

enableMapSet();

@Component({
    selector: 'app-root',
    imports: [
    RouterOutlet,
    TranslateModule,
    MatSidenavModule,
    NavBarComponent,
    SidePanelComponent,
    FooterComponent,
    NewsFeedBannerComponent
],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'ClassIT';
  dialogWebSubscriptionAgreementOpen = false
  dialogRef: MatDialogRef<any, any>;

  @ViewChild('drawer') drawer!: MatDrawer;
  private readonly userStore = inject(UserStore);
  private readonly companyStore = inject(CompanyStore);
  private readonly subscriptionStore = inject(SubscriptionStore);
  private readonly commonStore = inject(CommonStore);
  private readonly commonFunctionsService = inject(CommonFunctionsService);
  matDialog = inject(MatDialog);
  openedDialog: boolean = false;

  private readonly isInternalUser$: Signal<boolean> = this.userStore.isInternalUser;
  private readonly isAccountManager$: Signal<boolean> = this.userStore.isAccountManager;
  private readonly isCompanyAdmin$: Signal<boolean> = this.userStore.isCompanyAdmin;
  private readonly isWebsiteSubscriptionAvailable$: Signal<boolean> = this.subscriptionStore.websiteSubscriptionAvailable;
  private readonly isWebsiteSubscriptionExpired$: Signal<boolean> = this.subscriptionStore.websiteSubscriptionExpired;

  constructor(
    private translate: TranslateService,
    private errorModalService: ErrorModalService,
    private userInactivityService: UserInactivityService,
    private orchestrator: InactivityWarningOrchestratorService,
    protected router: Router,
  ) {
    this.userStore.initAuth();
    this.translate.addLangs(['en', 'fr', 'es']);
    this.translate.setDefaultLang('en');
    this.translate.use(this.translate.getBrowserLang() || 'en');
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      document.querySelector('.root-container')?.scrollTo({ top: 0 });
    });

    effect(() => {
      let allowEULADialog = true;
      const user = this.userStore.user();
      const currentRoute = this.commonFunctionsService.currentRoute$();

      if (user.userId && currentRoute !== '') {
        const allowedExpiredBaseRoutes = [
          '/profile/company-settings',
          '/profile/user-profile',
          '/purchase'
        ];
        
        if (allowedExpiredBaseRoutes.filter(x => currentRoute.includes(x)).length > 0) 
          return;

        if (!this.companyStore.fetched()) {
          this.companyStore.loadCompanyInfo();
        } else if (!this.subscriptionStore.fetched()) {
          this.subscriptionStore.loadSubscriptions();
        } else {
          // Determine if the user needs to be forced out of the app or into the company/subscription settings/purchase page
          if (this.isWebsiteSubscriptionAvailable$()) {
            if (this.isWebsiteSubscriptionExpired$()) {
              this.showExpiredWebSubscriptionDialog();
              allowEULADialog = false; // Do not activate EULA acceptance dialog if not accepted for some reason before expire.
            }
          }
        }
      }

      if (allowEULADialog) {
        const webSubscriptionId = this.userStore.webSubscriptionId();
        if (webSubscriptionId) {
          // This webSubscriptionId property is only available if the user didn't agree to the EULA yet.
          const pdfUrl = new URL('/api/subscriptions/GetWebsiteSubscriptionEulaDocument', environment.apiUrl).href;

          this.dialogWebSubscriptionAgreementOpen = true;
          if (!this.openedDialog) {
            this.dialogRef = this.matDialog.open(DialogComponent, {
              width: '80%',
              maxWidth: '72.5rem',
              height: '80%',
              maxHeight: '60rem',
              data: {
                title: this.translate.instant('PURCHASE.PLEASE_REVIEW_YOUR_SUBSCRIPTION_AGREEMENT'),
                icon: 'description',
                content: PdfViewerModalComponent,
                dataForComponent: {
                  url: pdfUrl,
                  forEulaAgreement: true,
                  subscriptionId: webSubscriptionId
                },
                hideClose: true,
                useMobileActions: true
              },
              disableClose: true
            });
            this.openedDialog = true;
            this.openDialog();
          }
        }
      }

      if (user.userId && this.commonStore.variables().idleTimeInMinutes) {
        this.userInactivityService.startMonitoring();
        this.orchestrator.start();
      }
    });
  }

  ngOnInit() {
    const setViewportHeightVar = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    window.addEventListener('resize', setViewportHeightVar);
    setViewportHeightVar();

    this.commonFunctionsService.initializeRouteTracking();
  }

  toggleDrawer() {
    this.drawer.toggle();
  }

  private openDialog() {
    if (!this.dialogRef) return;
    this.dialogRef.afterOpened().subscribe(() => {
      const instance = this.dialogRef.componentInstance;
      const contentInstance = instance.contentComponentRef.instance;

      if (contentInstance instanceof PdfViewerModalComponent) {
        contentInstance.closeModal.subscribe(() => {
          this.dialogRef.close();
          this.dialogWebSubscriptionAgreementOpen = false
        });
      }
    });
  }

  private showExpiredWebSubscriptionDialog(): void {
    if (this.isInternalUser$())
      return;

    if (this.dialogRef && this.dialogRef.getState() === MatDialogState.OPEN)
      this.dialogRef.close();

    // Configure message
    let translationKey = 'Generic_ExpirationModalMessage_Customer';
    if (this.isAccountManager$() || this.isCompanyAdmin$())
      translationKey = 'Generic_ExpirationModalMessage_CompanyAdminAccManager';

    // Configure actions
    let actions: Button[] = [];
    if (this.isAccountManager$() || this.isCompanyAdmin$()) {
      // The user logged in with an inactive website subscription.
      //  Account Manager/Company Admin -> Company Settings/Manage Subscriptions
      actions = [
        {
          id: 'companySettings',
          label: 'Generic_CompanySettings',
          styleType: 'contained',
          color: 'accent',
          onClick: async (): Promise<void> => {
            await this.router.navigate(['/profile/company-settings']);
            this.dialogRef.close();
          },
        },
        {
          id: 'userProfile',
          label: 'Generic_UserProfile',
          styleType: 'contained',
          color: 'accent',
          onClick: async (): Promise<void> => {
            await this.router.navigate(['/profile/user-profile']);
            this.dialogRef.close();
          },
        },
        {
          id: 'purchase',
          label: 'Generic_Purchase',
          styleType: 'contained',
          color: 'accent',
          onClick: async (): Promise<void> => {
            await this.router.navigate(['/purchase']);
            this.dialogRef.close();
          },
        }
      ];
    } else {
      //  Customer -> Show message and log out
      actions.push({
        id: 'logout',
        label: 'Generic_Logout',
        styleType: 'contained',
        color: 'error',
        onClick: async (): Promise<void> => {
          await this.commonFunctionsService.handleLogout();
          this.dialogRef.close();
        },
      })
    }

    // Build modal
    this.dialogRef = this.matDialog.open(DialogComponent, {
      data: {
        title: this.translate.instant('Generic_SubscriptionExpired'),
        icon: 'description',
        description: '',
        content: '',
        message: this.translate.instant(translationKey),
        hideClose: true,
        useMobileActions: true,
        actions: actions,
      },
      disableClose: true
    });
  }
}
